import { Track } from "@/src/lib/tracking";
/**
 * 收藏功能数据中心 - 核心类
 *
 * 实现多用户数据隔离、状态缓存、操作暂存和防抖处理等关键功能
 */

// import dayjs from "dayjs";
import { OspToast } from "@ospweb/sdk-ui";
import { proxy } from "valtio";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/store/favorite/FavoritesCenter";
import { rootStore } from "../rootStore";
import {
  BatchAddAssetsHandler,
  CheckBatchStatusHandler,
  DeletePositionAccountHandler,
  FetchFolderContentsHandler,
  FetchUserFoldersHandler,
  SyncPositionDataHandler,
  UpdateFavoriteHandler,
} from "./handlers";
import {
  CacheManager,
  OperationDebouncer,
  OptimisticUpdateManager,
  UserStateManager,
} from "./managers";
import type {
  FavoritesCenterState,
  FolderContentsOptions,
  FolderInfo,
  ListPositionAssetRequest,
  OperationResult,
  UserData,
} from "./types";

import { PositionsAccountService } from "./PositionsAccountService";
import type {
  AddPositionAsset,
  MemberClient,
  SyncPositionDataRequest,
} from "@features/api/memberApi";

const I18n = I18nUtil.createScoped(Translations);

class FavoritesCenter {
  private static instance: FavoritesCenter;
  private sourceData: Map<string, UserData> = new Map(); // 源数据：userId -> UserData
  private currentUserId: string | null = null;
  private positionsAccountService: PositionsAccountService | null = null; // Positions Account 服务实例

  // Managers - initialized in constructor (don't depend on API client)
  private userStateManager: UserStateManager;
  private cacheManager: CacheManager;
  private optimisticUpdateManager: OptimisticUpdateManager;
  private operationDebouncer: OperationDebouncer;

  // Handlers
  private fetchUserFoldersHandler: FetchUserFoldersHandler | null = null;
  private checkBatchStatusHandler: CheckBatchStatusHandler | null = null;
  private updateFavoriteHandler: UpdateFavoriteHandler | null = null;
  private fetchFolderContentsHandler: FetchFolderContentsHandler | null = null;
  private batchAddAssetsHandler: BatchAddAssetsHandler | null = null;
  private deletePositionAccountHandler: DeletePositionAccountHandler | null =
    null;
  private syncPositionDataHandler: SyncPositionDataHandler | null = null;

  // 响应式状态管理
  public state = proxy<FavoritesCenterState>({
    currentUserId: null,
    userFolders: undefined,

    // 使用版本化数据结构
    favoriteStates: {
      data: {},
      version: 0,
      lastUpdated: 0,
      isLoading: false, // 初始加载状态
    },
    allFavoriteData: undefined,

    // 内部状态
    isLoading: false,
    error: null,
  });

  // 添加静态变量用于去重
  private static fetchUserFoldersPromise: Promise<OperationResult> | null =
    null;
  private static lastFetchTime = 0;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5分钟

  /**
   * 获取单例实例
   */
  static getInstance(): FavoritesCenter {
    if (!FavoritesCenter.instance) {
      FavoritesCenter.instance = new FavoritesCenter();
    }
    return FavoritesCenter.instance;
  }

  /**
   * 私有构造函数 - 初始化 Managers（不依赖 API client）
   */
  private constructor() {
    // Initialize managers immediately (they don't depend on API client)
    this.userStateManager = new UserStateManager(this.sourceData, this.state);
    this.cacheManager = new CacheManager(
      this.sourceData,
      this.state,
      () => this.getCurrentUserId(), // 使用箭头函数动态获取 currentUserId
    );
    this.optimisticUpdateManager = new OptimisticUpdateManager(
      this.state,
      () => this.getCurrentUserId(), // 使用箭头函数动态获取 currentUserId
      (userId) => this._getOrCreateUserData(userId),
    );
    // OperationDebouncer needs positionsAccountService, so initialize with null
    this.operationDebouncer = new OperationDebouncer(
      null, // Will be updated in setApiClient
      (contentId, folderId, action) => {
        if (this.optimisticUpdateManager && this.operationDebouncer) {
          this.optimisticUpdateManager.rollbackFavoriteStatus(
            contentId,
            folderId,
            action,
            (cid, fid) =>
              this.operationDebouncer!.clearOperationHistory(cid, fid),
          );
        }
      },
      (error) => this.handleError(error),
      // New dependencies for state re-application after API success
      this.userStateManager,
      this.optimisticUpdateManager,
      this.state,
      () => this.getCurrentUserId(),
      (folderId, action) => this._updateUserFoldersAssetCount(folderId, action),
      (finalAction) => {
        if (finalAction === "ADD") {
          this.showFavoriteAddSuccessToast();
        }
      },
    );
    // 打印当前时间
    // log.debug(
    //   "[FavoritesCenter] FavoritesCenter initialized",
    //   this.userStateManager,
    //   this.cacheManager,
    //   this.optimisticUpdateManager,
    //   this.operationDebouncer,
    //   dayjs.toString(),
    // );
  }

  /**
   * 设置API客户端
   */
  public setApiClient(memberApi: MemberClient<any>): void {
    this.positionsAccountService = new PositionsAccountService(memberApi);

    // Update OperationDebouncer with the new service
    if (this.operationDebouncer) {
      this.operationDebouncer.updatePositionsAccountService(
        this.positionsAccountService,
      );
    }
    // Initialize handlers (需要 positionsAccountService)
    this.initializeHandlers();
  }

  /**
   * Initialize all handlers
   * 注意：此方法只能在 positionsAccountService 已初始化后调用
   */
  private initializeHandlers(): void {
    if (!this.positionsAccountService) {
      Track.log(
        "warn",
        "FavoritesCenter",
        "Cannot initialize handlers: positionsAccountService is null",
      );
      return;
    }
    this.fetchUserFoldersHandler = new FetchUserFoldersHandler(
      this.userStateManager,
      this.cacheManager,
      this.positionsAccountService,
      (folders) => this._sortUserFoldersByCreatedAt(folders),
      () => this._handleUserSwitch(),
      () => this.getCurrentUserId(),
      (loading) => {
        this.state.isLoading = loading;
      },
      (error) => {
        this.state.error = error;
      },
      (error) => this.handleError(error),
    );

    this.checkBatchStatusHandler = new CheckBatchStatusHandler(
      this.userStateManager,
      this.cacheManager,
      this.optimisticUpdateManager,
      this.state,
      this.positionsAccountService,
      (portfolioIds) => this._filterNormalStatusPortfolios(portfolioIds),
      () => this._handleUserSwitch(),
      () => this.getCurrentUserId(),
      (error) => this.handleError(error),
    );

    this.updateFavoriteHandler = new UpdateFavoriteHandler(
      this.userStateManager,
      this.optimisticUpdateManager,
      this.operationDebouncer,
      this.state,
      () => this.getCurrentUserId(),
      (folderId, action) => this._updateUserFoldersAssetCount(folderId, action),
    );

    this.fetchFolderContentsHandler = new FetchFolderContentsHandler(
      this.userStateManager,
      this.optimisticUpdateManager,
      this.state,
      this.positionsAccountService,
      (portfolioIds) => this._filterNormalStatusPortfolios(portfolioIds),
      () => this._handleUserSwitch(),
      () => this.getCurrentUserId(),
      (error) => this.handleError(error),
    );

    this.batchAddAssetsHandler = new BatchAddAssetsHandler(
      this.userStateManager,
      this.optimisticUpdateManager,
      this.state,
      this.positionsAccountService,
      () => this.getCurrentUserId(),
      (folderId, action, count) =>
        this._updateUserFoldersAssetCount(folderId, action, count),
    );

    this.deletePositionAccountHandler = new DeletePositionAccountHandler(
      this.positionsAccountService,
      () => this.getCurrentUserId(),
      (userId) => this._getOrCreateUserData(userId),
      (filter) => {
        if (this.state.userFolders) {
          this.state.userFolders = this.state.userFolders.filter((folder) =>
            filter(folder.id),
          );
        }
      },
      (error) => this.handleError(error),
    );

    this.syncPositionDataHandler = new SyncPositionDataHandler(
      this.positionsAccountService,
      async () => {
        await this.fetchUserFolders({ forceRefresh: true });
      },
      (error) => this.handleError(error),
    );
  }

  // ==================== 用户状态管理 ====================

  /**
   * 获取当前登录用户ID
   */
  private getCurrentUserId(): string | null {
    // 从 rootStore 获取当前登录用户ID
    return rootStore.user.ownerInfo?.id || null;
  }

  /**
   * 设置当前用户ID（触发响应式更新）
   */
  public setCurrentUserId(userId: string | null): void {
    this.userStateManager.setCurrentUserId(userId, (newUserId) => {
      this.currentUserId = newUserId;
      this.handleUserIdChange();
    });
  }

  /**
   * 响应式处理用户ID变化
   */
  private handleUserIdChange(): void {
    const newUserId = this.state.currentUserId;

    if (newUserId === null) {
      // 用户登出，清空激活数据
      this.userStateManager.clearActiveData();
    } else {
      // 用户切换，更新激活数据
      this.userStateManager.updateActiveDataFromSource(newUserId);
    }
  }

  /**
   * 处理用户切换的核心方法
   */
  private async _handleUserSwitch(): Promise<void> {
    await this.userStateManager.handleUserSwitch(this.currentUserId, (userId) =>
      this.setCurrentUserId(userId),
    );
  }

  /**
   * 获取或创建用户数据
   */
  private _getOrCreateUserData(userId: string): UserData {
    return this.userStateManager.getOrCreateUserData(userId);
  }

  // ==================== 缓存管理 ====================

  /**
   * 获取未缓存的内容ID列表
   */
  public getUncachedContentIds(contentIds: string[]): string[] {
    return this.cacheManager.getUncachedContentIds(contentIds);
  }

  /**
   * 检查收藏夹列表缓存是否有效
   * @param cacheTimeout 缓存超时时间（毫秒）
   * @returns 缓存是否有效
   */
  public isUserFoldersCacheValid(
    cacheTimeout: number = 5 * 60 * 1000,
  ): boolean {
    return this.cacheManager.isUserFoldersCacheValid(cacheTimeout);
  }

  /**
   * 清除收藏夹列表缓存
   */
  public clearUserFoldersCache(): void {
    this.cacheManager.clearUserFoldersCache();
  }

  /**
   * 获取 allFavoriteData 中未缓存的内容ID列表
   */
  public getUncachedAllFavoriteContentIds(contentIds: string[]): string[] {
    if (!this.state.allFavoriteData) {
      return contentIds; // 如果 allFavoriteData 未初始化，所有内容都未缓存
    }

    return contentIds.filter(
      (id) => this.state.allFavoriteData!.data[id] === undefined,
    );
  }

  /**
   * 获取用户收藏夹列表（支持缓存和强制刷新，内置去重机制）
   * @param options 查询选项
   * @returns 收藏夹列表结果
   */
  public async fetchUserFolders(options?: {
    limit?: number;
    forceRefresh?: boolean;
    cacheTimeout?: number;
  }): Promise<OperationResult> {
    const now = Date.now();
    const { forceRefresh = false } = options || {};

    // 检查缓存（除非强制刷新）
    if (
      !forceRefresh &&
      now - FavoritesCenter.lastFetchTime < FavoritesCenter.CACHE_DURATION &&
      this.state.userFolders
    ) {
      return { success: true, data: this.state.userFolders };
    }

    // 去重并发请求
    if (FavoritesCenter.fetchUserFoldersPromise) {
      return FavoritesCenter.fetchUserFoldersPromise;
    }

    // 创建新请求
    FavoritesCenter.fetchUserFoldersPromise =
      this._executeUserFoldersFetch(options);

    try {
      const result = await FavoritesCenter.fetchUserFoldersPromise;
      FavoritesCenter.lastFetchTime = now;
      return result;
    } finally {
      FavoritesCenter.fetchUserFoldersPromise = null;
    }
  }

  /**
   * 执行用户收藏夹获取的核心逻辑
   */
  private async _executeUserFoldersFetch(options?: {
    limit?: number;
    forceRefresh?: boolean;
    cacheTimeout?: number;
  }): Promise<OperationResult> {
    if (!this.fetchUserFoldersHandler) {
      return {
        success: false,
        error: "Handler not initialized. Please call setApiClient first.",
      };
    }

    try {
      return await this.fetchUserFoldersHandler.execute(options);
    } catch (error) {
      this.handleError(error as Error);
      return {
        success: false,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  /**
   * 批量查询收藏状态
   * @param contentIds 内容ID数组
   * @param assetType 资产类型 (TOKEN | STOCK)
   */
  public async checkBatchFavoritesStatus(
    contentIds: string[],
    assetType: "TOKEN" | "STOCK" = "TOKEN",
    accountId?: string,
  ): Promise<void> {
    // 延迟 200ms 执行，确保 handler 初始化成功
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          if (!this.checkBatchStatusHandler) {
            Track.log(
              "warn",
              "FavoritesCenter",
              "CheckBatchStatusHandler not initialized or contentIds is empty or accountId is empty",
              {
                accountId,
                id: String(this.state.userFolders),
                checkBatchStatusHandler: this.checkBatchStatusHandler,
              },
            );
            return resolve();
          }
          // 防止竞态问题，如果accountId为空，则先获取用户收藏夹列表，写入缓存中
          if (
            !this.state.userFolders ||
            this.state.userFolders.length === 0 ||
            !this.state.userFolders?.[0]?.id ||
            !accountId
          ) {
            await this.fetchUserFolders();
          }
          await this.checkBatchStatusHandler.execute(
            contentIds,
            assetType,
            accountId,
          );
          resolve();
        } catch (error) {
          this.handleError(error as Error);
          reject(error);
        }
      }, 200);
    });
  }

  /**
   * 更新收藏状态
   * @param contentId 内容ID
   * @param folderId 收藏夹ID very important: folderId is accountId
   * @param action 操作类型
   * @param assetType 资产类型 (TOKEN | STOCK)
   */
  public async updateFavoriteStatus(
    contentId: string,
    folderId: string,
    action: "ADD" | "REMOVE",
    assetType: "TOKEN" | "STOCK" = "TOKEN",
  ): Promise<void> {
    if (!this.updateFavoriteHandler || !contentId) {
      Track.log(
        "warn",
        "FavoritesCenter",
        "UpdateFavoriteHandler not initialized or folderId or contentId is empty. Operation skipped.",
        {
          updateFavoriteHandler: String(this.updateFavoriteHandler),
          folderId,
          contentId,
          action,
          assetType,
        },
      );
      return;
    }
    try {
      if (
        !this.state.userFolders ||
        this.state.userFolders.length === 0 ||
        !this.state.userFolders?.[0]?.id ||
        !folderId
      ) {
        await this.fetchUserFolders();
      }
      this.updateFavoriteHandler.execute(
        contentId,
        folderId || this.state.userFolders?.[0]?.id || "",
        action,
        assetType,
      );
    } catch (error) {
      Track.error(error, {
        tag: "FavoritesCenter",
        msg: "Failed to update favorite status:",
      });
      this.handleError(error as Error);
    }
  }

  /**
   * 批量添加收藏
   * @param assets 资产数组，每个资产包含 asset_id 和 asset_type
   * @param folderId 收藏夹ID (accountId)
   * @returns Promise<OperationResult> 操作结果
   */
  public async batchAddAssets(
    assets: AddPositionAsset[],
    folderId: string,
  ): Promise<OperationResult> {
    if (
      !this.batchAddAssetsHandler ||
      !folderId ||
      !assets ||
      assets.length === 0
    ) {
      Track.log(
        "warn",
        "FavoritesCenter",
        "BatchAddAssetsHandler not initialized or folderId or assets is empty",
      );
      return Promise.resolve({
        success: false,
        error: "Handler not initialized or folderId or assets is empty",
      });
    }
    try {
      const result = await this.batchAddAssetsHandler.execute(assets, folderId);
      if (result.success && (result.data?.count ?? 0) > 0) {
        this.showFavoriteAddSuccessToast();
      }
      return result;
    } catch (error) {
      this.handleError(error as Error);
      return Promise.resolve({
        success: false,
        error: (error as Error).message || "Unknown error",
      });
    }
  }

  /**
   * 删除持仓账户
   * @param accountId 账户ID
   * @returns Promise<OperationResult> 操作结果
   */
  public async deletePositionAccount(
    accountId: string,
  ): Promise<OperationResult> {
    if (!this.deletePositionAccountHandler) {
      Track.log(
        "warn",
        "FavoritesCenter",
        "DeletePositionAccountHandler not initialized. Please call setApiClient first.",
      );
      return Promise.resolve({
        success: false,
        error: "Handler not initialized. Please call setApiClient first.",
      });
    }

    try {
      return await this.deletePositionAccountHandler.execute(accountId);
    } catch (error) {
      this.handleError(error as Error);
      return Promise.resolve({
        success: false,
        error: (error as Error).message || "Unknown error",
      });
    }
  }

  /**
   * 同步持仓数据
   * @param accountId 账户ID
   * @param data 同步请求数据
   * @returns Promise<OperationResult> 操作结果
   */
  public async syncPositionData(
    accountId: string,
    data?: SyncPositionDataRequest,
  ): Promise<OperationResult> {
    if (!this.syncPositionDataHandler) {
      Track.log(
        "warn",
        "FavoritesCenter",
        "SyncPositionDataHandler not initialized. Please call setApiClient first.",
      );
      return Promise.resolve({
        success: false,
        error: "Handler not initialized. Please call setApiClient first.",
      });
    }

    try {
      return await this.syncPositionDataHandler.execute(accountId, data);
    } catch (error) {
      this.handleError(error as Error);
      return Promise.resolve({
        success: false,
        error: (error as Error).message || "Unknown error",
      });
    }
  }

  // ==================== 内存管理 ====================

  /**
   * 清理过期数据
   */
  private cleanupExpiredData(): void {
    const now = Date.now();
    const expireTime = 24 * 60 * 60 * 1000; // 24小时

    this.sourceData.forEach((userData, userId) => {
      if (now - userData.lastAccess > expireTime) {
        this.sourceData.delete(userId);
      }
    });
  }

  /**
   * 限制内存使用
   */
  private limitMemoryUsage(): void {
    const maxUsers = 5; // 最多缓存5个用户数据

    if (this.sourceData.size > maxUsers) {
      const oldestUser = Array.from(this.sourceData.entries()).sort(
        (a, b) => a[1].lastAccess - b[1].lastAccess,
      )[0];

      this.sourceData.delete(oldestUser[0]);
    }
  }

  /**
   * 过滤收藏夹ID，只保留 status = "NORMAL" 的收藏夹
   * @param portfolioIds 收藏夹ID数组
   * @returns 过滤后的收藏夹ID数组
   */
  private _filterNormalStatusPortfolios(portfolioIds: string[]): string[] {
    if (!this.state.userFolders || portfolioIds.length === 0) {
      return [];
    }
    return portfolioIds;

    // // 创建收藏夹状态映射：folderId -> status
    // const folderStatusMap = new Map<string, string>();
    // this.state.userFolders.forEach((folder) => {
    //   folderStatusMap.set(folder.id, folder.status);
    // });

    // // 只保留 status = "NORMAL" 的收藏夹
    // return portfolioIds.filter((portfolioId) => {
    //   const status = folderStatusMap.get(portfolioId);
    //   return status === "NORMAL";
    // });
  }

  // ==================== 错误处理 ====================

  /**
   * Single success toast after favorite ADD is confirmed by API (single-item flow).
   * Batch flow calls this from batchAddAssets after execute() succeeds.
   */
  private showFavoriteAddSuccessToast(): void {
    OspToast.success({
      content: I18n.t("Added! Ed is now tracking this for you."),
    });
  }

  /**
   * 统一错误处理
   */
  public handleError(error: Error): void {
    Track.error(error, { tag: "FavoritesCenter", msg: "Error:" });
    this.state.error = error.message;
  }

  /**
   * 清除错误状态
   */
  public clearError(): void {
    this.state.error = null;
  }

  // ==================== 收藏夹内容查询方法 ====================

  /**
   * 获取收藏夹内容列表（支持分页和缓存）
   * @param folderId 收藏夹ID 查询所有的话 folderId 为 all
   * @param options 查询选项
   * @returns 收藏夹内容结果
   */
  public async fetchFolderContents(
    folderId: string,
    options?: FolderContentsOptions & ListPositionAssetRequest,
  ): Promise<OperationResult & { next_token?: string }> {
    if (!this.fetchFolderContentsHandler) {
      return {
        success: false,
        error: "Handler not initialized. Please call setApiClient first.",
      };
    }
    if (
      !this.state.userFolders ||
      this.state.userFolders.length === 0 ||
      !this.state.userFolders?.[0]?.id
    ) {
      await this.fetchUserFolders();
    }
    try {
      return this.fetchFolderContentsHandler.execute(folderId, options);
    } catch (error) {
      this.handleError(error as Error);
      return Promise.resolve({
        success: false,
        error: (error as Error).message || "Unknown error",
      });
    }
  }

  // ==================== 私有辅助方法 ====================

  /**
   * 对 userFolders 按创建时间倒序排序
   * 注意：保持第一个账户（应该是 EDGEN）在第一位，其他账户按创建时间倒序排序
   * @param folders 收藏夹列表（第一个应该是 EDGEN 账户）
   * @returns 排序后的收藏夹列表
   */
  private _sortUserFoldersByCreatedAt(folders: FolderInfo[]): FolderInfo[] {
    if (folders.length === 0) {
      return folders;
    }

    // 保持第一个账户（EDGEN）在第一位
    const firstAccount = folders[0];
    const restAccounts = folders.slice(1);

    // 对其他账户按创建时间倒序排序
    const sortedRest = [...restAccounts].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateA - dateB; // （最新的在最后）
    });

    // 返回：第一个账户 + 排序后的其他账户
    return [firstAccount, ...sortedRest];
  }

  /**
   * 启动定期清理任务
   */
  public startCleanupTasks(): void {
    // 每小时清理一次过期数据
    setInterval(
      () => {
        this.cleanupExpiredData();
        this.limitMemoryUsage();
      },
      60 * 60 * 1000,
    );

    Track.log("info", "FavoritesCenter", "启动定期清理任务");
  }

  /**
   * 销毁数据中心
   */
  public destroy(): void {
    // 清空所有计时器
    this.operationDebouncer.clearAllTimers();

    // 清空数据
    this.sourceData.clear();
    this.userStateManager.clearActiveData();

    Track.log("info", "FavoritesCenter", "数据中心已销毁");
  }

  /**
   * 乐观更新 userFolders 中的 asset_count
   * @param folderId 收藏夹ID
   * @param action 操作类型
   * @param count 批量数量，默认 1
   */
  private _updateUserFoldersAssetCount(
    folderId: string,
    action: "ADD" | "REMOVE",
    count: number = 1,
  ): void {
    if (!this.state.userFolders) {
      return;
    }

    // 找到对应的收藏夹
    const folderIndex = this.state.userFolders.findIndex(
      (folder) => folder.id === folderId,
    );

    if (folderIndex === -1) {
      Track.log("warn", "FavoritesCenter", `Folder not found: ${folderId}`);
      return;
    }

    // 创建新的 userFolders 数组（不可变更新）
    const newUserFolders = [...this.state.userFolders];
    const folder = { ...newUserFolders[folderIndex] };

    // 更新 asset_count（支持批量数量）
    if (action === "ADD") {
      folder.asset_count = Math.max(0, folder.asset_count + count);
    } else {
      folder.asset_count = Math.max(0, folder.asset_count - count);
    }

    newUserFolders[folderIndex] = folder;

    // 更新响应式状态
    this.state.userFolders = newUserFolders;

    // 同时更新源数据
    if (this.currentUserId) {
      const userData = this._getOrCreateUserData(this.currentUserId);
      if (userData.userFolders) {
        const sourceFolderIndex = userData.userFolders.findIndex(
          (f) => f.id === folderId,
        );
        if (sourceFolderIndex !== -1) {
          userData.userFolders[sourceFolderIndex] = { ...folder };
        }
      }
    }
  }
}
// 导出单例实例
export const favoritesCenter = FavoritesCenter.getInstance();
